/**
 * Thème d'affichage des bulles sur la vidéo / capture (éditeur + centre d'aide).
 * Valeur persistée par commentaire (étape) : `commentaire_theme`, avec repli sur le tutoriel pour les anciennes données.
 */

export function normalizeCommentaireTheme(value) {
  return value === 'clair' ? 'clair' : 'sombre';
}

/** Thème effectif pour une bulle : par commentaire, avec repli sur le tutoriel (anciennes données). */
export function effectiveCommentaireTheme(comment, tutorial) {
  if (comment && comment.commentaire_theme != null && String(comment.commentaire_theme).trim() !== '') {
    return normalizeCommentaireTheme(comment.commentaire_theme);
  }
  return normalizeCommentaireTheme(tutorial?.commentaire_theme);
}

/** Classes Tailwind pour le cadre de la bulle (fond + bordure). `style` = entrée COMMENT_STYLES[type]. */
export function commentBubbleFrameClasses(theme, style) {
  const s = style || null;
  const t = normalizeCommentaireTheme(theme);
  if (!s) {
    return t === 'clair' ? 'bg-white border-2 border-blue-500' : 'bg-slate-800 border-2 border-blue-500';
  }
  if (t === 'clair') {
    return `bg-white border-2 ${s.borderColor}`;
  }
  return `${s.bgLight} border-2 ${s.borderColor}`;
}

export function commentBubblePrimaryTextClass(theme) {
  return normalizeCommentaireTheme(theme) === 'clair' ? 'text-black' : 'text-slate-100';
}

/** Bornes de l’échelle du texte rendu (bulle capture / aide / aplati). */
export const TEXTE_RENDU_SCALE_MIN = 0.65;
export const TEXTE_RENDU_SCALE_MAX = 1.45;

/** 1 = taille par défaut ; hors bornes ou invalide → replis sur 1. */
export function clampTexteRenduScale(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.min(TEXTE_RENDU_SCALE_MAX, Math.max(TEXTE_RENDU_SCALE_MIN, Math.round(n * 1000) / 1000));
}

/**
 * Style inline pour la typo de bulle (complète `tutorialStepBubbleTextClassList`).
 * @param {{ stable16?: boolean, texteRenduScale?: number|null }} options
 */
export function tutorialStepBubbleTextStyle(_theme, options = {}) {
  const scale = clampTexteRenduScale(options.texteRenduScale);
  if (Math.abs(scale - 1) < 0.001) return {};
  const stable = options.stable16 === true;
  if (stable) {
    return {
      fontSize: `calc(clamp(1.0625rem, 3.25vmin, 1.75rem) * ${scale})`,
    };
  }
  /* Mode non stable : base équivalente à text-sm (0.875rem) */
  return { fontSize: `calc(0.875rem * ${scale})` };
}

/**
 * Bulle d’étape / commentaire sur capture : mêmes classes éditeur + TutorialStepStage
 * (césure, alignement, interligne) pour un rendu identique sur /aide/tutoriel.
 * @param {{ stable16?: boolean, softWrap?: boolean }} [options] — stable16 / softWrap : pas de clip, pas de min-w-0 (évite césures au milieu des mots)
 */
export function tutorialStepBubbleShellClassList(theme, style, options = {}) {
  const softLayout = options.stable16 === true || options.softWrap === true;
  const overflow = softLayout ? 'overflow-visible' : 'overflow-hidden';
  const pad = options.stable16 === true ? 'p-4 sm:p-5' : 'p-3';
  const box = softLayout
    ? 'absolute z-20 box-border shrink-0 rounded-lg shadow-xl'
    : 'absolute z-20 box-border min-w-0 rounded-lg shadow-xl';
  return [box, overflow, pad, commentBubbleFrameClasses(theme, style)].filter(Boolean).join(' ');
}

/**
 * @param {{ stable16?: boolean, softWrap?: boolean }} [options]
 * — stable16 : typo plus grande (rendu stable)
 * — softWrap : comme stable pour les césures (espaces / tirets), sans agrandir la typo (éditeur mode étapes)
 */
export function tutorialStepBubbleTextClassList(theme, options = {}) {
  const stable = options.stable16 === true;
  const softWrap = stable || options.softWrap === true;
  const sizeLeading = stable
    ? 'text-[clamp(1.0625rem,3.25vmin,1.75rem)] font-medium leading-snug sm:leading-normal tracking-normal [-webkit-font-smoothing:antialiased] [text-rendering:geometricPrecision]'
    : 'text-sm font-medium leading-relaxed';
  const flow = softWrap
    ? 'm-0 block w-max max-w-full text-left whitespace-pre-wrap hyphens-none [overflow-wrap:normal] [word-break:normal]'
    : 'm-0 block w-full min-w-0 text-left whitespace-pre-wrap break-words';
  return [flow, sizeLeading, commentBubblePrimaryTextClass(theme)].filter(Boolean).join(' ');
}
